import React, { useState } from "react";
import { X, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";
import { CATEGORY_LABELS, CURRENCIES } from "./FinancePage.utils";

/**
 * Iter92 — Modal unificado "Nuevo Registro".
 * El usuario elige Ingreso vs Egreso con un toggle al inicio. Resto del form
 * (monto / moneda / descripción / categoría / fecha) es idéntico para ambos.
 * Si recibe `type="income"` o `type="expense"` lo usa como selección inicial,
 * pero el toggle siempre se muestra para que pueda corregir.
 */
export function AddEntryModal({ type = "income", onClose, onSaved }) {
  const [kind, setKind] = useState(type === "expense" ? "expense" : "income");
  const isExpense = kind === "expense";
  const [form, setForm] = useState({
    amount: "", currency: "UYU", description: "",
    category: "general", date: new Date().toISOString().slice(0, 10),
  });
  const [loading, setLoading] = useState(false);

  const save = async () => {
    if (!form.amount || !form.description.trim()) { toast.error("Completá todos los campos"); return; }
    setLoading(true);
    try {
      const endpoint = isExpense ? `${API}/admin/finance/expense` : `${API}/admin/finance/income`;
      const res = await authFetch(endpoint, {
        method: "POST",
        body: JSON.stringify({ ...form, amount: parseFloat(form.amount) }),
      });
      const d = await res.json();
      if (d.ok) { toast.success(`${isExpense ? "Egreso" : "Ingreso"} registrado`); onSaved(); onClose(); }
      else toast.error("Error al guardar");
    } catch { toast.error("Error de conexión"); } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-[#ffd16c]/40 transition-colors";

  return (
    <div
      className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
      data-testid="add-entry-modal"
    >
      <div className="bg-[#1a1a1a] border border-white/10 rounded-3xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-black text-white">Nuevo Registro</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-xl transition-colors"
                  data-testid="add-entry-close-btn">
            <X size={14} className="text-gray-400" />
          </button>
        </div>

        {/* Toggle Ingreso / Egreso */}
        <div className="grid grid-cols-2 gap-2 mb-4 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06]">
          <button
            onClick={() => setKind("income")}
            data-testid="add-entry-kind-income"
            className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-black transition-all ${
              !isExpense
                ? "bg-[#ffd16c] text-black shadow-md"
                : "text-gray-400 hover:bg-white/[0.04]"
            }`}
          >
            <ArrowUpCircle size={14} /> Ingreso
          </button>
          <button
            onClick={() => setKind("expense")}
            data-testid="add-entry-kind-expense"
            className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-black transition-all ${
              isExpense
                ? "bg-[#ff7351] text-white shadow-md"
                : "text-gray-400 hover:bg-white/[0.04]"
            }`}
          >
            <ArrowDownCircle size={14} /> Egreso
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 block font-semibold">Monto</label>
              <input type="number" step="0.01" min="0" value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                placeholder="0.00" className={inputCls}
                data-testid="add-entry-amount" />
            </div>
            <div className="w-28">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 block font-semibold">Moneda</label>
              <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                className={inputCls} data-testid="add-entry-currency">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 block font-semibold">Descripción</label>
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder={isExpense ? "Ej: Factura Cloudflare" : "Ej: Pago cliente García"}
              className={inputCls}
              data-testid="add-entry-description" />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 block font-semibold">Categoría</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className={inputCls} data-testid="add-entry-category">
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5 block font-semibold">Fecha</label>
              <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className={inputCls} data-testid="add-entry-date" />
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
                  className="flex-1 py-3 rounded-xl bg-white/[0.05] text-gray-400 text-sm font-bold hover:bg-white/[0.08] transition-colors">
            Cancelar
          </button>
          <button onClick={save} disabled={loading}
            data-testid="add-entry-save-btn"
            className={`flex-1 py-3 rounded-xl text-sm font-black transition-all disabled:opacity-60 ${
              isExpense ? "bg-[#ff7351] hover:brightness-110 text-white" : "bg-[#ffd16c] hover:brightness-110 text-black"
            }`}>
            {loading ? "Guardando..." : `Guardar ${isExpense ? "Egreso" : "Ingreso"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
